import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import EmptyState from '../components/EmptyState'

interface AuditEvent {
  audit_id: string
  action: string
  actor: string
  detail?: Record<string, unknown>
  created_at: string
}

interface AuditResponse {
  audits: AuditEvent[]
  count: number
  next_key?: string
}

const ACTION_LABELS: Record<string, string> = {
  manual_reply:       'Respuesta manual',
  config_update:      'Cambio de configuración',
  document_review:    'Revisión de documento',
  purge_requested:    'Purga solicitada',
  outreach_sent:      'Outreach enviado',
  login:              'Inicio de sesión',
  logout:             'Cierre de sesión',
}

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  manual_reply:    { bg: 'var(--c-info-light)', color: 'var(--c-info-ink)' },
  config_update:   { bg: 'var(--c-warn-light)',  color: 'var(--c-warn)' },
  document_review: { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  purge_requested: { bg: 'var(--c-danger-light)',  color: 'var(--c-danger)' },
  outreach_sent:   { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
}

const DEFAULT_COLOR = { bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)' }

function fmtDate(ts?: string) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('es-PE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function ActionBadge({ action }: { action: string }) {
  const style = ACTION_COLORS[action] ?? DEFAULT_COLOR
  const label = ACTION_LABELS[action] ?? action
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  )
}

function DetailCell({ detail }: { detail?: Record<string, unknown> }) {
  if (!detail || Object.keys(detail).length === 0) return <span style={{ color: 'var(--c-ink-faint)' }}>—</span>
  return (
    <span style={{ fontSize: 12, color: 'var(--c-ink-muted)', fontFamily: 'monospace' }}>
      {Object.entries(detail)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join(' · ')}
    </span>
  )
}

export default function Audit() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nextKey, setNextKey] = useState<string | undefined>()
  const [loadingMore, setLoadingMore] = useState(false)
  const [search, setSearch] = useState('')

  const fetchAudit = useCallback(async (cursor?: string) => {
    try {
      const params: Record<string, string> = { limit: '50' }
      if (cursor) params.last_key = cursor
      const res = await apiClient.get<AuditResponse>('/audit', { params })
      setEvents(prev => cursor ? [...prev, ...res.data.audits] : res.data.audits)
      setNextKey(res.data.next_key)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar auditoría')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  useEffect(() => { fetchAudit() }, [fetchAudit])

  const loadMore = () => {
    if (!nextKey) return
    setLoadingMore(true)
    fetchAudit(nextKey)
  }

  const filtered = search.trim()
    ? events.filter(e =>
        e.actor.toLowerCase().includes(search.toLowerCase()) ||
        e.action.toLowerCase().includes(search.toLowerCase()) ||
        (ACTION_LABELS[e.action] ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : events

  return (
    <div>

      {error && <ErrorAlert message={error} />}

      {/* Buscador */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Buscar por actor o acción…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid var(--c-surface-strong)',
            fontSize: 14,
            width: 320,
            outline: 'none',
          }}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="📋"
          title="Sin eventos de auditoría"
          description={search ? 'Ningún evento coincide con la búsqueda.' : 'Aún no hay eventos registrados en el sistema.'}
        />
      ) : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--c-surface-border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--c-surface-subtle)', borderBottom: '1px solid var(--c-surface-border)' }}>
                  {['Fecha y hora', 'Actor', 'Acción', 'Detalle'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontWeight: 600, color: 'var(--c-ink-muted)', fontSize: 13,
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((ev, i) => (
                  <tr
                    key={ev.audit_id}
                    style={{
                      borderBottom: '1px solid var(--c-surface-faint)',
                      background: i % 2 === 0 ? '#fff' : 'var(--c-surface-alt)',
                    }}
                  >
                    <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', color: 'var(--c-ink-muted)', fontSize: 13 }}>
                      {fmtDate(ev.created_at)}
                    </td>
                    <td style={{ padding: '10px 16px', fontWeight: 500, color: 'var(--c-ink)' }}>
                      {ev.actor}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <ActionBadge action={ev.action} />
                    </td>
                    <td style={{ padding: '10px 16px', maxWidth: 400 }}>
                      <DetailCell detail={ev.detail} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--c-ink-muted)' }}>
              {filtered.length} evento{filtered.length !== 1 ? 's' : ''}
              {search ? ' (filtrados)' : ''}
            </span>
            {nextKey && !search && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                style={{
                  padding: '6px 16px', borderRadius: 8, border: '1px solid var(--c-surface-strong)',
                  background: '#fff', cursor: loadingMore ? 'not-allowed' : 'pointer',
                  fontSize: 13, color: 'var(--c-ink-soft)',
                }}
              >
                {loadingMore ? 'Cargando…' : 'Cargar más →'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
